import React, { useState } from 'react';
import { ShieldCheck, Crosshair, AlertTriangle, CheckCircle, Search, HelpCircle, FileCheck, Clipboard } from 'lucide-react';

export const NexoraxLegalAuditNode: React.FC = () => {
  const [textToScan, setTextToScan] = useState('');
  const [auditResults, setAuditResults] = useState<any[] | null>(null);

  // THE NEXORAX COMPLIANCE CHECKLIST (UK/SCOTS LAW MANDATES)
  const MANDATORY_TOKENS = [
    { id: 1, label: 'Corporate Identity', token: 'NexoraX Digital Ltd', critical: true },
    { id: 2, label: 'Company Number (SC)', token: 'SC887095', critical: true },
    { id: 3, label: 'Jurisdiction', token: 'Scotland', critical: true },
    { id: 4, label: 'Support Gateway', token: 'support@nexoraxdigital.co.uk', critical: true },
    { id: 5, label: 'NexoraX Phone Line', token: '08000 622827', critical: true },
    { id: 6, label: 'GDPR Article 17 (Erasure)', token: 'Article 17', critical: true },
    { id: 7, label: 'Liability Cap', token: 'greater of £100', critical: true },
    { id: 8, label: 'ICO Reference Label', token: 'ICO Data Protection', critical: true },
    { id: 9, label: 'VAT/HMRC Audit Retention', token: '6 years', critical: true },
    { id: 10, label: 'Zero-Extraction Guarantee', token: 'As-Is', critical: true },
  ];

  // Advanced script fallback loop for flexible string evaluation
  const runAuditWithFuzzyLogic = (rawText: string) => {
    return MANDATORY_TOKENS.map(item => {
      const cleanText = rawText.toLowerCase();
      
      // Check for exact match OR standard alternative phrasings
      let passed = cleanText.includes(item.token.toLowerCase());
      
      if (item.id === 9 && !passed) {
        // If "6 years" fails, check if the accountant spelled it out textually
        passed = cleanText.includes('six years');
      }
      
      return { ...item, passed };
    });
  };

  const saveAuditLogToVault = async (score: number) => {
    const logPayload = {
      timestamp: new Date().toISOString(),
      action_token: 'VAULT_SYNC',
      operator_signature: 'Office of the Lead Architect',
      sha256_checksum: `SCORE_SIG_${score}_${Math.random().toString(36).substring(7).toUpperCase()}`
    };
    
    // Routes log to your private repository cluster
    await fetch('/api/ledger/audit-trail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(logPayload)
    });
  };

  const runAudit = () => {
    const results = runAuditWithFuzzyLogic(textToScan);
    setAuditResults(results);
    const passed = results.filter(r => r.passed).length;
    const score = Math.floor((passed / MANDATORY_TOKENS.length) * 100);
    saveAuditLogToVault(score).catch(err => console.error("Vault sync failed:", err));
  };

  const handlePasteDemo = () => {
    setTextToScan(
      `PRIVACY POLICY & TERMS OF SERVICE\n\n` +
      `This policy governs the cognitive data distribution models of NexoraX Digital Ltd, an institution incorporated under Registry Number SC887095 in Scotland.\n\n` +
      `Under our Zero-Extraction Guarantee, all calculations, files, and outputs are provided completely As-Is to guarantee total data privacy on your local workstation.\n\n` +
      `For structural inquiries, remote node synchronisations, or data protection concerns under GDPR Article 17 (Erasure), please reach out directly to our dedicated support@nexoraxdigital.co.uk gateway.\n\n` +
      `Additionally, direct voice support can be accessed via our Secure NexoraX Phone Line at 08000 622827. \n\n` +
      `All records, accounts, and audits are maintained in strict alignment with UK and Scottish local laws, observing a VAT/HMRC Audit Retention period of at least 6 years. Out of advanced security prudence, our liability cap under this document is restricted to the greater of £100. Our operations are fully logged with the ICO Data Protection registration directory.`
    );
  };

  // Calculate Score
  const passedCount = auditResults ? auditResults.filter(r => r.passed).length : 0;
  const totalScore = Math.floor((passedCount / MANDATORY_TOKENS.length) * 100);

  return (
    <div className="w-full max-w-5xl mx-auto p-4 md:p-6 bg-slate-950/95 border border-slate-800 rounded-2xl font-mono text-slate-200 shadow-2xl relative overflow-hidden">
      {/* Background ambient light */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 blur-[80px] pointer-events-none"></div>
      
      <div className="mb-6 border-b border-white/5 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-1.5 text-cyan-400 font-bold text-[9px] uppercase tracking-[0.2em] mb-1">
            <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-ping"></span>
            <span>GDPR & UK/SCOTS COMPLIANCE MODULE</span>
          </div>
          <h1 className="text-lg md:text-xl font-bold text-white uppercase tracking-wider flex items-center gap-2">
            <ShieldCheck size={20} className="text-cyan-500" />
            NEXORAX LEGAL AUDIT NODE
          </h1>
          <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wide">
            Direct Compliance Verification Engine | Office of the Director Lead Director
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePasteDemo}
            className="px-2.5 py-1 bg-indigo-950/40 hover:bg-indigo-900/40 border border-indigo-700/30 text-indigo-300 rounded font-mono text-[9px] font-bold uppercase transition-all flex items-center gap-1.5"
          >
            <Clipboard size={11} /> Load Compliant Demo
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        
        {/* INPUT ZONE: Paste your Policy/Terms here */}
        <div className="lg:col-span-7 flex flex-col h-full">
          <label className="block text-[10px] font-black text-slate-400 mb-2 uppercase tracking-widest flex items-center gap-1.5">
            <Crosshair size={12} className="text-slate-500" />
            DOCUMENT ANALYSIS LAYER (PASTE LAWS / TERMS / POLICIES):
          </label>
          <textarea
            value={textToScan}
            onChange={(e) => setTextToScan(e.target.value)}
            placeholder="Copy and paste your entire Privacy Policy, legal terms or company documents here to run structural verification scan..."
            className="w-full flex-1 min-h-[340px] p-4 bg-black/40 border border-slate-800 rounded-xl text-[11px] text-slate-300 focus:outline-none focus:border-cyan-500/80 focus:ring-1 focus:ring-cyan-500/20 font-mono transition-colors resize-y leading-relaxed"
          />
          <button
            onClick={runAudit}
            className="mt-4 w-full py-3 bg-gradient-to-r from-[#003c4f] via-[#00516a] to-[#015e7a] hover:from-cyan-900 hover:to-cyan-800 text-[#00ffd2] font-black font-mono text-[10px] uppercase tracking-widest rounded-xl transition-all shadow-lg border border-cyan-500/30 glow-cyan cursor-pointer flex items-center justify-center gap-2"
          >
            <Search size={13} className="animate-pulse" />
            <span>🔍 EXECUTE NEXORAX COMPLIANCE SCAN</span>
          </button>
        </div>

        {/* RESULTS ZONE: Pass/Fail Dashboard */}
        <div className="lg:col-span-5 bg-[#030611] rounded-2xl border border-slate-900 p-5 flex flex-col justify-between h-full">
          <div>
            <div className="flex justify-between items-center mb-4 border-b border-white/5 pb-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                <FileCheck size={13} className="text-cyan-400" />
                AUDIT COMPLIANCE MATRIX REPORT
              </span>
              {auditResults && (
                <span className={`text-xs font-black font-mono px-2.5 py-0.5 rounded uppercase border ${
                  totalScore === 100 
                    ? 'text-emerald-400 border-emerald-950 bg-emerald-950/20 shadow-[0_0_15px_rgba(16,184,129,0.15)] animate-bounce' 
                    : 'text-amber-400 border-amber-950 bg-amber-950/20'
                }`}>
                  {totalScore}% COMPLIANT
                </span>
              )}
            </div>

            {!auditResults ? (
              <div className="h-64 flex flex-col items-center justify-center text-slate-500 text-xs italic gap-2 border border-dashed border-slate-800/60 rounded-xl p-4 bg-slate-950/50">
                <HelpCircle size={18} className="text-slate-650 animate-pulse" />
                <span className="uppercase text-[9px] text-slate-500 tracking-wider">WAITING FOR LEGAL INPUT STREAM...</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1.5 custom-scrollbar">
                {auditResults.map((result) => (
                  <div key={result.id} className="flex items-center justify-between p-2 bg-[#020308] rounded-xl border border-slate-900/80 transition-all hover:bg-[#03050d] hover:border-slate-800/60">
                    <div className="text-left select-none">
                      <p className="text-[10.5px] font-black text-slate-300">{result.label}</p>
                      <p className="text-[8.5px] text-slate-550 lowercase tracking-tight mt-0.5">Searching string phrase: <span className="text-slate-400 font-mono">"{result.token}"</span></p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 select-none">
                      {result.passed ? (
                        <span className="px-2 py-0.5 bg-emerald-950/30 text-emerald-400 text-[8.5px] font-black border border-emerald-900/30 rounded uppercase tracking-wider">PASS</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-rose-950/40 text-rose-400 text-[8.5px] font-black border border-rose-900/30 rounded uppercase tracking-wider animate-pulse flex items-center gap-1">
                          <AlertTriangle size={8} /> MISSING
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {auditResults && (
            <div className="mt-4 pt-4 border-t border-white/5 select-none text-left">
              {totalScore === 100 ? (
                <div className="p-3 bg-emerald-950/20 border border-emerald-500/30 rounded-xl">
                  <div className="flex items-center gap-1.5 text-emerald-400 font-bold text-[10px] uppercase">
                    <CheckCircle size={13} className="text-emerald-400" />
                    NEXORAX COMPLIANCE GUARANTEE MET
                  </div>
                  <p className="text-[8.5px] text-slate-400 uppercase mt-1 leading-normal font-semibold">
                    Absolute release verified. All mandatory UK GDPR, Companies House SC887095 reference tokens, and Scots Law jurisdiction definitions are legally accounted for.
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-amber-950/20 border border-amber-500/30 rounded-xl">
                  <div className="flex items-center gap-1.5 text-amber-500 font-bold text-[10px] uppercase">
                    <AlertTriangle size={13} className="text-amber-500" />
                    CRITICAL EXPOSURE DEFICIENCIES
                  </div>
                  <p className="text-[8.5px] text-slate-400 uppercase mt-1 leading-normal font-semibold">
                    Do not publish or transmit documents. Rectify missing elements immediately to maintain enterprise-grade corporate compliance.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
